import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PatternLock from "react-pattern-lock";
import UAParser from "ua-parser-js";
import { motion, AnimatePresence } from "framer-motion";
import { useAdminLoggedInMutation } from "../redux/features/admin/adminApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const schema = Yup.object().shape({
  email: Yup.string().email("Invalid email!").required("Please enter your email!"),
  password: Yup.string().required("Please enter your password!").min(6),
});

const Login = () => {
  const [adminLoggedIn, { isSuccess, isError, error, data }] = useAdminLoggedInMutation();
  const [path, setPath] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [newPath, setNewPath] = useState("");
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const fetchIP = async () => {
    const res = await fetch("https://api.ipify.org");
    const data = await res.text();
    localStorage.setItem("ip", data);
    return data;
  };

  function getpath() {
    if (!path || path.length === 0) return;

    let newPathString = "";
    for (let x of path) {
      const num = parseInt(x);
      if (!isNaN(num)) newPathString += (num + 1).toString();
    }

    if (newPathString.length < 3) {
      toast.error("This pattern is too short, Please try again!");
      setNewPath("");
      setPath([]);
      return;
    }
    setNewPath(newPathString);
  }

  const getDeviceInfo = () => {
    const parser = new UAParser();
    const result = parser.getResult();
    return {
      userAgent: navigator.userAgent,
      os: result.os.name,
      browser: result.browser.name,
      device: result.device.model || "unknown",
      os_version: result.os.version,
      browser_version: result.browser.version,
    };
  };

  const reset = () => {
    setDisabled(false);
    setPath([]);
  };

  useEffect(() => {
    localStorage.clear();
    if (isSuccess && data?.status === true) {
      localStorage.setItem("token", data.token);
      localStorage.setItem("permission", JSON.stringify(data.permission));
      localStorage.setItem("role", JSON.stringify(data.role));
      localStorage.setItem("ip", data.ip);
      toast.success(data.message);
      navigate("/");
    } else if (isSuccess && data?.status !== true) {
      toast.error(data.message);
    }
  }, [isSuccess, isError, data, error, navigate]);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: schema,
    onSubmit: async (values) => {
      const mydata = {
        deviceInfo: getDeviceInfo(),
        ipaddress: { ip: await fetchIP() },
        username: values.email,
        password: values.password,
        pattern: newPath,
      };
      await adminLoggedIn(mydata);
      reset();
    },
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden p-8 relative">
        <h2 className="text-3xl font-bold text-blue-800 text-center mb-6">
          Admin Login
        </h2>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col gap-5"
            >
              {/* Email */}
              <div className="flex flex-col gap-1">
                <label htmlFor="email" className="text-sm font-semibold text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  placeholder="admin@example.com"
                  value={values.email}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 text-sm outline-none"
                />
                {errors.email && touched.email && (
                  <p className="text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1">
                <label htmlFor="password" className="text-sm font-semibold text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  placeholder="Enter your password"
                  value={values.password}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-400 text-sm outline-none"
                />
                {errors.password && touched.password && (
                  <p className="text-xs text-red-500">{errors.password}</p>
                )}
              </div>

              {/* Next button */}
              <button
                type="button"
                onClick={() => {
                  if (!values.email || !values.password) {
                    toast.error("Please fill in email and password!");
                    return;
                  }
                  setStep(2);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg py-2 transition-all duration-300 shadow-sm"
              >
                Next
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center gap-6"
            >
              <label className="font-semibold text-sm text-blue-900">
                Draw Your Pattern
              </label>

              <div className="flex justify-center items-center">
                <div className="p-3 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-md">
                  <div className="relative flex items-center justify-center">
                    <PatternLock
                      path={path}
                      width={280} // ensures grid fits perfectly
                      size={3}
                      connectorThickness={6} // thicker for clear line
                      connectorColor="#ffffff"
                      disabled={disabled}
                      onChange={(newPath) => {
                        if (Array.isArray(newPath)) setPath(newPath);
                      }}
                      onFinish={() => {
                        getpath();
                        setDisabled(false);
                      }}
                      pointActiveColor="#fff"
                      pointColor="#c0d4ff"
                      pointRadius={10}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-1 rounded-full border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="px-4 py-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition"
                >
                  Sign In
                </button>
              </div>

              <button
                type="button"
                className="text-xs text-blue-600 underline mt-1"
                onClick={reset}
              >
                Reset Pattern
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Login;
